import { Paper, Stack } from "@mui/material";
import { useTheme } from "design-system/themeContext";
import "react-grid-layout/css/styles.css";
import "rsuite/dist/rsuite-no-reset.min.css";
import "react-resizable/css/styles.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import { deletePortfolioDashboardConfigs, postPortfolioDashboardConfigs } from "../api/functions/all";
import { useGetPortfolioDashboardConfigs, useGetPortfolioData } from "../api/hooks/allHooks";
import { Table } from "../components";
import { AssetCardModal } from "../components/AssetOverview/AssetCardModal";
import { DynamicDashboardMultiTab } from "../components/chart/DynamicDashboardMultiTab";
import { LazyLoad } from "../components/LazyLoad";
import { LoadingOrEmptyWrapper } from "../components/LoadingAndEmptyHandler";
import { getTechnicalDataSectionDisplayName } from "../configs/TechnicalDataEnums";
import {
  allDataGroupedColumns,
  APP_NAME_IN_REDUX,
  categoricalColumns,
  columnsAPINamesEnums,
  columnsDisplayNamesEnums,
  columnsUnits,
  dropdownFilters,
  FiltersNames,
  FinancialDataColsNamesEnums,
  getDefaultColors,
  getPortfolioColumnDisplayName,
  isRelevantOption,
  numbersScalesTypes,
  numericRangeFilters,
  PORTFOLIO_NAME_IN_API_BODY,
  RetrifitsColsNamesEnums,
} from "../enums/AssetOverview/portfolio";
import { TechnicalDataColsNamesEnums } from "../enums/AssetOverview/portfolioTechnicalDataPart";
import { FilterTypes } from "../enums/components";
import { chartTypesEnums, colorCategoriesEnums } from "../enums/dynamicDashboard";
import { eventNames, trackGAEvent } from "../functions/googleAnalytics";
import { getTableTypicalColumn } from "../utils/components";
import { distinctFilter, findDataRange, isNumeric } from "../utils/dataManipulation";
import {
  defaultIsVisible,
  defaultSortFunction,
  dynamicDashboardGetRelatedItems,
  dynamicDashboardIsItemHovered,
  getChartFiltersCreators,
  getColorEntity,
  getDynamicDahboardDefaultSettings,
  getTabFiltersCreators,
  handleConfigChange,
  settingsOptions,
} from "../utils/dynamicDashboard";
import {
  dataAndRetrofitsMultiSelectFilterFunctionByRetrofitsKeys,
  dataFinancialDataMultiSelectFilterFunctionByFinancialGroups,
  dataFinancialDataRangeFilterFunctionByFinancialKeys,
  dataMultiSelectFilterFunctionByTechnicalDataKeys,
  handleAdjustingAggregatedDataPointsFilterByGroupDataPoints as handleAdjustingAggregatedDataPointsFilterByGroupDataPoints,
  handleGroupingTransformation,
  handleNestingTransformation,
  handleScalingTransformation,
  refinePortfolioData,
  removeTheEffectOfGroupsFromConfig,
  summarizeCoolingSystemData,
  summarizeHeatingSystemData,
  summarizeWindowData,
  useGetDataColumnsOptions,
  useGetRetrofitValuesOptions,
  useGetTechnicalDataValuesOptions,
} from "../utils/portfolio";
import { flattenPortfolioData3Level } from "../utils/portfolio";

const filtersCategories = [
  {
    categoryName: "Building profile",
    filtersNames: [
      columnsAPINamesEnums.PORTFOLIO,
      columnsAPINamesEnums.BUILDING_ID,
      columnsAPINamesEnums.BUILDING_NAME,
      columnsAPINamesEnums.USE_TYPE,
      columnsAPINamesEnums.BOUGHT_ON,
      columnsAPINamesEnums.SOLD_ON,
      columnsAPINamesEnums.RENTAL_UNITS,
      columnsAPINamesEnums.YEAR_OF_CONSTRUCTION,
      columnsAPINamesEnums.FLOOR_AMOUMT,
      columnsAPINamesEnums.HERITAGE_PROTECTED,
      columnsAPINamesEnums.BUILDING_TYPE_ID,
      columnsAPINamesEnums.BUILDING_TYPE,
      columnsAPINamesEnums.PRE_FABRICATED,
      columnsAPINamesEnums.LOGGIA,
      columnsAPINamesEnums.BALCONY,
      columnsAPINamesEnums.FOOTPRINT,
      columnsAPINamesEnums.CEILING_AREA,
      columnsAPINamesEnums.FACADE_AREA,
      columnsAPINamesEnums.PREMISE,
      columnsAPINamesEnums.GROSS_AREA_WITH_BASEMENT,
      columnsAPINamesEnums.GROSS_LETTABLE_AREA_WIGHOUT_BASEMENT,
      columnsAPINamesEnums.NET_AREA_WITH_BASEMENT,
      columnsAPINamesEnums.NET_LETTABEL_AREA_WITHOUT_BASEMENT,
      columnsAPINamesEnums.ENERGY_EFFICIENCY_CLASS,
      columnsAPINamesEnums.ENERGY_CONSUMPTION_RATIO,
      // columnsNamesEnums.ACCOUNTING_AREA,
    ],
  },
  {
    categoryName: "Technical Data",
    filtersNames: [...Object.values(TechnicalDataColsNamesEnums)],
  },
  {
    categoryName: "Geography",
    filtersNames: [
      columnsAPINamesEnums.ZIP,
      columnsAPINamesEnums.CITY,
      columnsAPINamesEnums.MAIN_DISTRICT,
      columnsAPINamesEnums.SUB_DISTRICT,
      columnsAPINamesEnums.FULL_ADDRESS,
      columnsAPINamesEnums.STREET,
    ],
  },
  {
    categoryName: "Energy & CO2",
    filtersNames: [
      columnsAPINamesEnums.EPC_CONSUMPTION_KWH_M2A,
      columnsAPINamesEnums.HEAT_ENERGY_CONSUMPTION_EPC_KWH_M2_A,
      columnsAPINamesEnums.HEAT_ENERGY_CONSUMPTION_EPC_KWH_A,
      columnsAPINamesEnums.ELECTRICITY_DEMAND_KWH_A,
      columnsAPINamesEnums.ELECTRICITY_DEMAND_KWH_M2A,
      columnsAPINamesEnums.HEAT_ENERGY_TYPE,
      columnsAPINamesEnums.DISTRICT_HEATING_AVAILABLE,
      columnsAPINamesEnums.NATURAL_GAS_AVAILABLE,
      columnsAPINamesEnums.TOTAL_ENERGY_CONSUMPTION,
      columnsAPINamesEnums.CO2_EMISSION,
      columnsAPINamesEnums.STRANDING_YEAR,
      // columnsAPINamesEnums.TOTALS__CONSUMPTION, // Can be added later
    ],
  },
  {
    categoryName: "Solar Energy",
    filtersNames: [
      columnsAPINamesEnums.DIFFUSE_SOLAR_PER_M2,
      columnsAPINamesEnums.DIFFUSE_SOLAR_TOTAL,
      columnsAPINamesEnums.DIRECT_SOLAR_PER_M2,
      columnsAPINamesEnums.DIRECT_SOLAR_TOTAL,
      columnsAPINamesEnums.GLOBAL_SOLAR_PER_M2,
      columnsAPINamesEnums.GLOBAL_SOLAR_TOTAL,
      columnsAPINamesEnums.LARGEST_ROOF_DIFFUSE_SOLAR_PER_M2,
      columnsAPINamesEnums.LARGEST_ROOF_DIFFUSE_SOLAR_TOTAL,
      columnsAPINamesEnums.LARGEST_ROOF_DIRECT_SOLAR_PER_M2,
      columnsAPINamesEnums.LARGEST_ROOF_DIRECT_SOLAR_TOTAL,
      columnsAPINamesEnums.LARGEST_ROOF_GLOBAL_SOLAR_PER_M2,
      columnsAPINamesEnums.LARGEST_ROOF_GLOBAL_SOLAR_TOTAL,
    ],
  },
  {
    categoryName: "Financial Data",
    filtersNames: [...Object.values(FinancialDataColsNamesEnums), columnsAPINamesEnums.FINANCIAL_DATA__TOTALS__ALL],
  },
  {
    categoryName: "Retrofits Characteristics",
    filtersNames: [
      ...Object.values(RetrifitsColsNamesEnums),
      //  columnsAPINamesEnums.RETROFITS__TOTALS__REDUCTIONS // Can be added later
    ],
  },
  {
    categoryName: "Groups",
    filtersNames: [
      // columnsAPINamesEnums.GROUPED__CONSUMPTION,  // Can be added later
      // columnsAPINamesEnums.GROUPED__RETROFITS__REDUCTIONS,  // Can be added later
      columnsAPINamesEnums.GROUPED__FINANCIAL_DATA__ALL,
    ],
  },
];

const dataColumnsOptionsGroups = filtersCategories?.map((category) => {
  const sortedOptions = [...category.filtersNames];

  sortedOptions.sort();
  return { group: category.categoryName, options: sortedOptions };
});
dataColumnsOptionsGroups.unshift({ group: "Count", options: [columnsAPINamesEnums.COUNT] });

const defaultPortfolioConfigsAndColorsConfigs = {
  colors: {
    Columns: {},
    Mixed: {},
    Values: {},
  },
  tabs: [
    {
      filters: {
        City: ["Berlin"],
        city: ["Berlin"],
        "CO2 Emissions": [0, 108.7],
        "Electrictiy consumption": [48.95, 54],
        "Energy consumption": [3.63, 66],
        "Energy Efficiency class": [null, "C", "A", "B"],
        "Gross area": [29000, 51430],
        "Heat energy consumption": [16.35, 123],
        "Stranding year": [2020, 2035],
        "Total Energy consumption": [37, 309.5618896484375],
      },
      selectedFiltersNames: ["City", "city"],
      settings: [
        {
          chartType: "miniMap",
          dataPoint: "energy_consumption",
          diagrams: [
            {
              color: "black",
              dataKey: null,
              dataPoint: null,
              diagramType: null,
              direction: "horizontal",
              partitions: null,
              percentageBased: false,
              stacks: null,
              title: null,
            },
          ],
          draggable: true,
          h: 7,
          id: 6,
          legendsPosition: "bottom",
          maxH: 20,
          minH: 3,
          minW: 3,
          sortValue: null,
          title: null,
          type: "1",
          w: 12,
          x: 0,
          xAxis: null,
          y: 0,
          yAxis: null,
        },
        {
          chartType: "histogram",
          dataPoint: null,
          diagrams: [
            {
              color: "black",
              dataKey: "gross_asset_basement_included",
              dataPoint: null,
              diagramType: null,
              direction: "horizontal",
              partitions: null,
              percentageBased: false,
              stacks: null,
              title: null,
            },
          ],
          draggable: true,
          h: 7,
          id: 7,
          legendsPosition: "bottom",
          maxH: 20,
          minH: 3,
          minW: 3,
          sortValue: null,
          title: "Gross Area Histogram",
          type: "1",
          w: 6,
          x: 6,
          xAxis: null,
          y: 7,
          yAxis: null,
        },
        {
          chartType: "histogram",
          dataPoint: "construction_year",
          diagrams: [
            {
              color: "black",
              dataKey: "construction_year",
              dataPoint: null,
              diagramType: null,
              direction: "horizontal",
              partitions: null,
              percentageBased: false,
              stacks: null,
              title: null,
            },
          ],
          draggable: true,
          h: 7,
          id: 8,
          legendsPosition: "bottom",
          maxH: 20,
          minH: 3,
          minW: 3,
          sortValue: null,
          title: "Year of construction",
          type: "1",
          w: 6,
          x: 0,
          xAxis: null,
          y: 7,
          yAxis: null,
        },
        {
          chartType: "doughnut",
          dataPoint: "Count",
          diagrams: [
            {
              color: "black",
              dataKey: null,
              dataPoint: null,
              diagramType: null,
              direction: "horizontal",
              partitions: "main_district",
              percentageBased: false,
              stacks: null,
              title: null,
            },
          ],
          draggable: true,
          h: 7,
          id: 9,
          legendsPosition: "right",
          maxH: 20,
          minH: 3,
          minW: 3,
          sortValue: null,
          title: null,
          type: "1",
          w: 6,
          x: 0,
          xAxis: null,
          y: 14,
          yAxis: null,
        },
        {
          aggregateXAxis: 0,
          chartType: "composed",
          dataPoint: null,
          diagrams: [
            {
              color: "black",
              dataKey: "carbon_dioxide_emission_value",
              dataPoint: null,
              diagramType: "scatter",
              direction: "horizontal",
              partitions: null,
              percentageBased: false,
              stacks: null,
              title: null,
            },
          ],
          draggable: true,
          h: 7,
          id: 10,
          legendsPosition: "bottom",
          maxH: 20,
          minH: 3,
          minW: 3,
          sortValue: null,
          title: "Energy vs. CO2",
          type: "1",
          w: 6,
          x: 6,
          xAxis: "energy_consumption",
          y: 14,
          yAxis: null,
        },
      ],
      tab: "Portfolio summary",
    },
  ],
};

const loadDashboardDefaultConfigs = () => {
  return defaultPortfolioConfigsAndColorsConfigs;
};

export const Portfolio = () => {
  const colorBarRange = useSelector(
    (state) => state.dynamicDashboard?.[APP_NAME_IN_REDUX]?.processedValues?.colorBarRange
  );

  useEffect(() => {
    trackGAEvent(eventNames.PORTFOLIO_VIEW);
  }, []);

  const { t } = useTranslation();
  const { user } = useSelector((store) => store.user);
  const userId = user?.user_id;
  const { data, isLoading } = useGetPortfolioData(userId);

  const dataPreprocessing = useMemo(() => {
    const { mappedData, mappedInfoBoxAndTableData } = refinePortfolioData({ data });
    return [mappedData, mappedInfoBoxAndTableData];
  }, [data]);

  const [generalDashboardData, infoBoxesAndTableData] = useMemo(() => dataPreprocessing, [dataPreprocessing]);

  const {
    allDataColumns,
    allDataColumnsAndGroupedColumnsDisplayOptions,
    allDataColumnsAndGroupedColumnsOptions,
    allDataColumnsDisplayOptions,
    numericalColumns,
    summarySections,
    summarySectionsDisplayOptions,
  } = useGetDataColumnsOptions(generalDashboardData);

  const [filteredData, setFilteredData] = useState(generalDashboardData);

  const headers = [
    getTableTypicalColumn({
      dataKey: columnsAPINamesEnums.BUILDING_NAME,
      label: columnsDisplayNamesEnums.BUILDING_NAME,
      valueClassName: "py-1",
      valueTransformation: (row) => (
        <LazyLoad height={50}>
          <Stack gap={2}>
            <img className="h-12 w-12 object-contain object-center" src={row[columnsAPINamesEnums.IMAGE_URL_VALUE]} />
            <span>{row[columnsAPINamesEnums.BUILDING_NAME]}</span>
          </Stack>
        </LazyLoad>
      ),
    }),
    ...[
      [columnsAPINamesEnums.PORTFOLIO, columnsDisplayNamesEnums.PORTFOLIO],
      [columnsAPINamesEnums.HEAT_ENERGY_TYPE, columnsDisplayNamesEnums.HEAT_ENERGY_TYPE],
      [columnsAPINamesEnums.USE_TYPE, columnsDisplayNamesEnums.USE_TYPE],
      [columnsAPINamesEnums.TOTAL_ENERGY_CONSUMPTION, columnsDisplayNamesEnums.TOTAL_ENERGY_CONSUMPTION],
      [columnsAPINamesEnums.CO2_EMISSION, columnsDisplayNamesEnums.CO2_EMISSION],
    ].map(([dataKey, label]) => getTableTypicalColumn({ dataKey, label })),
  ];

  const tableFilteredData = infoBoxesAndTableData.filter((tableDataRow) => {
    return filteredData?.some(
      (mainDataRow) => mainDataRow[columnsAPINamesEnums.ID] === tableDataRow[columnsAPINamesEnums.ID]
    );
  });

  let {
    data: savedPortfolioAndColorsConfigs,
    isError,
    isLoading: isSavedConfigLoading,
  } = useGetPortfolioDashboardConfigs(userId);

  const savedPortfolioAndColorsConfigsMemoized = useMemo(() => {
    return savedPortfolioAndColorsConfigs?.config ?? loadDashboardDefaultConfigs();
  }, [savedPortfolioAndColorsConfigs]);

  const retrofitValues = useGetRetrofitValuesOptions(generalDashboardData);
  const { componentValues, loanTypeValues, subComponentValues, typeOfMeasureValues } = retrofitValues;

  const technicalDataValues = useGetTechnicalDataValuesOptions(generalDashboardData);
  const { colorValues, energySourceValues, objectTypeValues, systemTypeValues, windowTypeValues } = technicalDataValues;

  const tabFilters = ({ allFiltersConfig, setConfig }) => {
    const { createDropdownFilter, createNumericRangeFilter, getTabFiltersItemCommonPart } = getTabFiltersCreators({
      allFiltersConfig,
      columnsDisplayNameFn: getPortfolioColumnDisplayName,
      generalDashboardData,
      setConfig,
    });

    const createRetrofitFilter = ({ name, options }) => {
      const commonPart = getTabFiltersItemCommonPart(name);
      return {
        ...(commonPart ?? {}),
        filterFunction: dataAndRetrofitsMultiSelectFilterFunctionByRetrofitsKeys,
        filterType: FilterTypes.DROPDOWN_MULTI,
        props: {
          ...(commonPart?.props ?? {}),
          options,
        },
      };
    };

    const createTechnicalDataFilter = ({ displayOptions, name, options }) => {
      const commonPart = getTabFiltersItemCommonPart(name);
      return {
        ...(commonPart ?? {}),
        filterFunction: dataMultiSelectFilterFunctionByTechnicalDataKeys,
        filterType: FilterTypes.DROPDOWN_MULTI,
        props: {
          ...(commonPart?.props ?? {}),
          displayOptions,
          options,
        },
      };
    };

    const retrofitFilters = [
      { name: columnsAPINamesEnums.RETROFITS__COMPONENT, options: componentValues },
      { name: columnsAPINamesEnums.RETROFITS__SUB_COMPONENT, options: subComponentValues },
      { name: columnsAPINamesEnums.RETROFITS__LOAN_TYPE, options: loanTypeValues },
      { name: columnsAPINamesEnums.RETROFITS__TYPE_OF_MESSURE, options: typeOfMeasureValues },
    ];

    const technicalDataFilters = [
      { name: columnsAPINamesEnums.TECHNICAL_DATA__COLOR, options: colorValues },
      {
        displayOptions: objectTypeValues?.map((value) => getTechnicalDataSectionDisplayName(value)),
        name: columnsAPINamesEnums.TECHNICAL_DATA__SUB_COMPONENT,
        options: objectTypeValues,
      },
      { name: columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_TYPE, options: windowTypeValues },
      { name: columnsAPINamesEnums.TECHNICAL_DATA__ENERGY_SOURCE, options: energySourceValues },
      { name: columnsAPINamesEnums.TECHNICAL_DATA__SYSTEM_TYPE, options: systemTypeValues },
    ];

    return [
      ...dropdownFilters.map(createDropdownFilter),
      ...numericRangeFilters.map(createNumericRangeFilter),
      ...retrofitFilters.map(createRetrofitFilter),
      ...technicalDataFilters.map(createTechnicalDataFilter),
    ];
  };

  const chartFilters = useCallback(
    (updateChart, config) => {
      const { createDropdownFilter, createNumericRangeFilter, getChartFiltersItemCommonPart } = getChartFiltersCreators(
        {
          columnsDisplayNameFn: getPortfolioColumnDisplayName,
          config,
          generalDashboardData,
          updateChart,
        }
      );

      const createSpecialFilter = ({ filterFunction, filterType = FilterTypes.DROPDOWN_MULTI, key, props = {} }) => {
        const commonPart = getChartFiltersItemCommonPart(key);
        return {
          ...(commonPart ?? {}),
          filterFunction,
          filterType: filterType,
          props: {
            ...(commonPart?.props ?? {}),
            ...props,
          },
        };
      };

      const specialFilters = [
        {
          filterFunction: dataFinancialDataRangeFilterFunctionByFinancialKeys,
          filterType: FilterTypes.NUMERIC_RANGE_PICKER,
          key: columnsAPINamesEnums.FINANCIAL_DATA__YEAR,
          props: { changeByTyping: true, max: 2100, min: 2000 },
        },
        {
          filterFunction: dataAndRetrofitsMultiSelectFilterFunctionByRetrofitsKeys,
          key: columnsAPINamesEnums.RETROFITS__COMPONENT,
          props: { options: componentValues },
        },
        {
          filterFunction: dataAndRetrofitsMultiSelectFilterFunctionByRetrofitsKeys,
          key: columnsAPINamesEnums.RETROFITS__SUB_COMPONENT,
          props: { options: subComponentValues },
        },
        {
          filterFunction: dataAndRetrofitsMultiSelectFilterFunctionByRetrofitsKeys,
          key: columnsAPINamesEnums.RETROFITS__LOAN_TYPE,
          props: { options: loanTypeValues },
        },
        {
          filterFunction: dataAndRetrofitsMultiSelectFilterFunctionByRetrofitsKeys,
          key: columnsAPINamesEnums.RETROFITS__TYPE_OF_MESSURE,
          props: { options: typeOfMeasureValues },
        },
        {
          filterFunction: dataFinancialDataMultiSelectFilterFunctionByFinancialGroups,
          key: columnsAPINamesEnums.GROUPED__FINANCIAL_DATA__ALL,
          props: {
            displayOptions: allDataGroupedColumns.GROUPED__FINANCIAL_DATA__all.columns.map((col) =>
              getPortfolioColumnDisplayName({ colName: col })
            ),
            options: allDataGroupedColumns.GROUPED__FINANCIAL_DATA__all.columns,
          },
        },
        {
          filterFunction: dataMultiSelectFilterFunctionByTechnicalDataKeys,
          key: columnsAPINamesEnums.TECHNICAL_DATA__COLOR,
          props: {
            options: colorValues,
          },
        },
        {
          filterFunction: dataMultiSelectFilterFunctionByTechnicalDataKeys,
          key: columnsAPINamesEnums.TECHNICAL_DATA__SUB_COMPONENT,
          props: {
            options: objectTypeValues,
          },
        },
        {
          filterFunction: dataMultiSelectFilterFunctionByTechnicalDataKeys,
          key: columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_TYPE,
          props: {
            options: windowTypeValues,
          },
        },
        {
          filterFunction: dataMultiSelectFilterFunctionByTechnicalDataKeys,
          key: columnsAPINamesEnums.TECHNICAL_DATA__ENERGY_SOURCE,
          props: {
            options: energySourceValues,
          },
        },
        {
          filterFunction: dataMultiSelectFilterFunctionByTechnicalDataKeys,
          key: columnsAPINamesEnums.TECHNICAL_DATA__SYSTEM_TYPE,
          props: {
            options: systemTypeValues,
          },
        },
      ];

      return [
        ...dropdownFilters.map(createDropdownFilter),
        ...numericRangeFilters.map(createNumericRangeFilter),
        ...specialFilters.map(createSpecialFilter),
      ];
    },
    [
      generalDashboardData,
      componentValues,
      subComponentValues,
      loanTypeValues,
      typeOfMeasureValues,
      colorValues,
      objectTypeValues,
      windowTypeValues,
      energySourceValues,
      systemTypeValues,
    ]
  );

  const isNumericalColumn = (colName) => !categoricalColumns?.includes(colName);

  const dynamicChartsRowHeight = 50;

  const getDataPointOptions = ({ config }) => {
    if (config.chartType === chartTypesEnums.SUMMARY) {
      return { displayOptions: summarySectionsDisplayOptions, options: summarySections };
    } else {
      return { displayOptions: allDataColumnsDisplayOptions, options: allDataColumns };
    }
  };
  const allSettings = useCallback(
    (updateChart, config) => [
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.TITLE, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.DESCRIPTION, updateChart }),
      {
        adjustConfig: (config) => config,
        filterLabel: "Data point",
        filterName: FiltersNames.DATAPOINT,
        filterType: FilterTypes.DROPDOWN_SINGLE,
        isInDiagramsPart: false,
        mainDataKey: "dataPoint",
        props: {
          displayOptions: getDataPointOptions({ config })?.displayOptions,
          label: "Data point",
          options: getDataPointOptions({ config })?.options,
          optionsGroups: dataColumnsOptionsGroups,
          width: "75%",
        },
        setConfig: (updateChart) => {
          return (key, value) => {
            updateChart(key, value);
            updateChart(settingsOptions.COLOR_BAR_RANGE, null);
          };
        },
        useAsDataFilter: false,
        visible: () => isRelevantOption(config.chartType, settingsOptions.DATA_POINT),
      },
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.Y_AXIS_NAME, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.SECOND_Y_AXIS_NAME, updateChart }),
      getDynamicDahboardDefaultSettings({
        config,
        override: {
          visible: () =>
            defaultIsVisible(config.chartType, settingsOptions.AGGREGATION_TYPE) &&
            !(config.chartType === chartTypesEnums.MINI_MAP && !isNumericalColumn(config[settingsOptions.DATA_POINT])),
        },
        settingName: settingsOptions.AGGREGATION_TYPE,
        updateChart,
      }),
      {
        adjustConfig: (config) => config,
        filterLabel: "Numbers Scale",
        filterName: FiltersNames.SCALING_TYPE,
        filterType: FilterTypes.DROPDOWN_SINGLE,
        isInDiagramsPart: false,
        props: {
          label: "Numbers Scale",
          options: [numbersScalesTypes.BUILDING_AREA, numbersScalesTypes.NO_SCALE, numbersScalesTypes.TOTAL_AREA],
          width: "75%",
        },
        setConfig: (updateChart) => updateChart,
        useAsDataFilter: false,
        visible: () =>
          defaultIsVisible(config.chartType, settingsOptions.SCALING_TYPE) &&
          !(config.chartType === chartTypesEnums.MINI_MAP && !isNumericalColumn(config[settingsOptions.DATA_POINT])),
      },
      {
        adjustConfig: (config) => config,
        filterLabel: "X Axis",
        filterName: FiltersNames.X_AXIS,
        filterType: FilterTypes.DROPDOWN_SINGLE,
        isInDiagramsPart: false,
        props: {
          className: "w-3/4",
          displayOptions: allDataColumnsAndGroupedColumnsDisplayOptions,
          label: "x-axis",
          options: allDataColumnsAndGroupedColumnsOptions,
          optionsGroups: dataColumnsOptionsGroups,
        },
        setConfig: (updateChart) => updateChart,
        visible: () => isRelevantOption(config.chartType, settingsOptions.X_AXIS),
      },
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.X_AXIS_NAME, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.INVERT_X_AXIS, updateChart }),
      {
        adjustConfig: (config) => config,
        filterLabel: "Y Axis",
        filterName: FiltersNames.Y_AXIS,
        filterType: FilterTypes.DROPDOWN_SINGLE,
        isInDiagramsPart: false,
        props: {
          className: "w-3/4",
          displayOptions: allDataColumnsAndGroupedColumnsDisplayOptions,
          label: "y-axis",
          options: allDataColumnsAndGroupedColumnsOptions,
          optionsGroups: dataColumnsOptionsGroups,
        },
        setConfig: (updateChart) => updateChart,
        visible: () => isRelevantOption(config.chartType, settingsOptions.Y_AXIS),
      },
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.INVERT_Y_AXIS, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.X_AXIS_RANGE, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.Y_AXIS_RANGE, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.SECOND_Y_AXIS_RANGE, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.LEGENDS_POSITION, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.SHOW_PIE_LABELS, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.SORT_VALUE, updateChart }),
      {
        adjustConfig: (config) => config,
        filterLabel: "Color bar range",
        filterName: settingsOptions.COLOR_BAR_RANGE,
        filterType: FilterTypes.NUMERIC_RANGE_PICKER,
        isInDiagramsPart: false,
        props: {
          changeByTyping: true,
          className: "block w-3/4 ",
          label: "Color bar range",
          ...findDataRange({ data: filteredData, dataKeys: [config?.dataPoint], defaultMin: 0 }),
          ...(isNumeric(colorBarRange?.[config?.id]?.max) && isNumeric(colorBarRange?.[config?.id]?.min)
            ? {
                max: colorBarRange?.[config?.id]?.max,
                min: colorBarRange?.[config?.id]?.min,
              }
            : {}),
        },
        setConfig: (updateChart) => updateChart,
        visible: () =>
          defaultIsVisible(config.chartType, settingsOptions.COLOR_BAR_RANGE) &&
          isNumericalColumn(config[settingsOptions.DATA_POINT]),
      },
      {
        adjustConfig: (config) => config,
        adjustSetConfigFn: (index) => (filterName, value) =>
          handleConfigChange(index, config.diagrams, filterName, value, updateChart),
        filterLabel: "Y Axis",
        filterName: FiltersNames.DIAGRAMS_DIAGRAM_DATA_KEY,
        filterType: FilterTypes.DROPDOWN_SINGLE,
        isInDiagramsPart: true,
        props: {
          className: "block w-3/4 ",
          displayOptions: allDataColumnsDisplayOptions,
          label: "y-axis",
          options: allDataColumns,
          optionsGroups: dataColumnsOptionsGroups,
        },
        visible: (diagramIndex) =>
          isRelevantOption(config.chartType, settingsOptions.DIAGRAMS_DIAGRAM_DATA_KEY, diagramIndex),
      },
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.DIAGRAM_TYPE, updateChart }),
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.DIAGRAMS_STACK_ID, updateChart }),
      getDynamicDahboardDefaultSettings({
        config,
        settingName: settingsOptions.DIAGRAMS_AS_SECOND_Y_AXIS,
        updateChart,
      }),
      {
        adjustConfig: (config) => config,
        adjustSetConfigFn: (index) => (filterName, value) =>
          handleConfigChange(index, config.diagrams, filterName, value, updateChart),
        filterLabel: "Partitions",
        filterName: FiltersNames.DIAGRAMS_PARTITIONS,
        filterType: FilterTypes.DROPDOWN_SINGLE,
        isInDiagramsPart: true,
        props: {
          className: "block w-3/4 ",
          displayOptions: allDataColumnsAndGroupedColumnsDisplayOptions,
          label: "Partitions",
          options: allDataColumnsAndGroupedColumnsOptions,
          optionsGroups: dataColumnsOptionsGroups,
        },
        visible: (diagramIndex) =>
          isRelevantOption(config.chartType, settingsOptions.DIAGRAMS_PARTITIONS, diagramIndex),
      },
      {
        adjustConfig: (config) => config,
        adjustSetConfigFn: (index) => (filterName, value) =>
          handleConfigChange(index, config.diagrams, filterName, value, updateChart),
        filterLabel: "Stacks",
        filterName: FiltersNames.DIAGRAMS_STACKS,
        filterType: FilterTypes.DROPDOWN_SINGLE,
        isInDiagramsPart: true,
        props: {
          className: "block w-3/4 ",
          displayOptions: allDataColumnsAndGroupedColumnsDisplayOptions,
          label: "Stacks",
          options: allDataColumnsAndGroupedColumnsOptions,
          optionsGroups: dataColumnsOptionsGroups,
        },
        visible: (diagramIndex) => isRelevantOption(config.chartType, settingsOptions.STACKS, diagramIndex),
      },
      getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.DIAGRAMS_DIRECTION, updateChart }),
    ],
    [
      allDataColumns,
      allDataColumnsAndGroupedColumnsDisplayOptions,
      allDataColumnsAndGroupedColumnsOptions,
      filteredData,
      JSON.stringify(colorBarRange),
      allDataColumnsDisplayOptions,
    ]
  );

  const memoizedSpecificData = useMemo(() => [], []);
  const useGetSpecificData = (_config) => {
    return { isError: false, isLoading: false, specificData: memoizedSpecificData };
  };
  const getDataTransformatorMemoDependencyArray = useCallback(({ config }) => {
    const { chartType, dataPoint, diagrams, scalingType, xAxis, yAxis } = config;

    return JSON.stringify([dataPoint, chartType, diagrams, xAxis, yAxis, scalingType]);
  }, []);

  const dataTransformator = useCallback(
    (notScaledData, config) => {
      const { chartType, dataPoint, diagrams, xAxis, yAxis } = config;

      let data = !notScaledData ? [] : [...notScaledData];

      let output = [];
      const { partitions, stacks } = (diagrams && diagrams[0]) || {};

      const aggregationAdjustedData = handleAdjustingAggregatedDataPointsFilterByGroupDataPoints({ data });

      data = handleNestingTransformation({
        allSettings,
        config,
        data: aggregationAdjustedData,
        dataPoint,
        diagrams,
        partitions,
        stacks,
        xAxis,
        yAxis,
      });

      let scaledData = handleScalingTransformation({ config, data, originalData: notScaledData });

      let groupedData = handleGroupingTransformation({
        allSettings,
        config,
        data: scaledData,
        dataPoint,
        diagrams,
        partitions,
        stacks,
        xAxis,
        yAxis,
      });

      if (chartType === chartTypesEnums.SUMMARY) {
        output = { flattenData: groupedData || [] };
      } else if ([chartTypesEnums.DOUGHNUT, chartTypesEnums.PIE].includes(chartType)) {
        const flattenData = groupedData?.map((row) => {
          const filteredRow = {};
          if (partitions) filteredRow[partitions] = row[partitions];
          if (stacks) filteredRow[stacks] = row[stacks];
          return {
            ...filteredRow,
            dataPoint,
            [dataPoint]: row[dataPoint],
          };
        });
        output = { flattenData: flattenData || [] };
      } else if (
        [chartTypesEnums.AREA, chartTypesEnums.BAR, chartTypesEnums.KPI, chartTypesEnums.LINE].includes(chartType)
      ) {
        const flattenData = groupedData?.map((row) => {
          const filteredRow = {};
          if (partitions) filteredRow[partitions] = row[partitions];
          if (stacks) filteredRow[stacks] = row[stacks];
          if (xAxis) filteredRow[xAxis] = row[xAxis];
          return {
            ...filteredRow,
            dataPoint,
            [dataPoint]: row[dataPoint],
          };
        });
        output = { flattenData: flattenData || [] };
      } else if (chartType === chartTypesEnums.HEATMAP) {
        const flattenData = groupedData?.map((row) => {
          const filteredRow = {};
          if (yAxis) filteredRow[yAxis] = row[yAxis];
          if (xAxis) filteredRow[xAxis] = row[xAxis];
          return {
            ...filteredRow,
            dataPoint,
            [dataPoint]: row[dataPoint],
          };
        });
        const xLabels = groupedData?.map((row) => row[xAxis])?.filter(distinctFilter);
        xLabels?.sort();

        const yLabels = groupedData?.map((row) => row[yAxis])?.filter(distinctFilter);
        yLabels?.sort();

        output = { flattenData: flattenData || [], xLabels, yLabels };
      } else {
        output = { flattenData: groupedData || [] };
      }

      return output;
    },
    [allSettings]
  );

  const getUnit = useCallback(({ quantity }) => columnsUnits?.[quantity], []);

  const IdColumnName = columnsAPINamesEnums.ID;

  const [triggerFlag, setTriggerFlag] = useState(false);

  const renderMiniMapObjectClickModal = ({ objects: clickedAssets, setObjects: setClickedAssets }) => {
    return clickedAssets?.map((clickedAsset) => {
      const id = clickedAsset[columnsAPINamesEnums.ID];
      const selectedBuilding = infoBoxesAndTableData?.find((building) => building?.[columnsAPINamesEnums.ID] === id);
      return (
        <AssetCardModal
          assetCardPosition={clickedAsset?.assetPosition}
          building={selectedBuilding}
          key={id}
          onCardClick={() => {
            window.open(`${window.location.origin}/details/${id}/buildingProfile`);
          }}
          onClose={() => setClickedAssets((clickedAssets) => clickedAssets.filter((asset) => asset?.id !== id))}
          triggerFlag={`${triggerFlag} ${id}`}
        />
      );
    });
  };

  const getColumnDisplayNameCallback = useCallback((props) => getPortfolioColumnDisplayName(props), []);

  const QueryClient = useQueryClient();
  const onSave = async (data) => {
    await postPortfolioDashboardConfigs({ data: data, userId });
    QueryClient.invalidateQueries(["portfolioDashboardConfigs", userId, PORTFOLIO_NAME_IN_API_BODY]);
  };

  const onReset = async () => {
    await deletePortfolioDashboardConfigs({ userId });
    QueryClient.invalidateQueries(["portfolioDashboardConfigs", userId, PORTFOLIO_NAME_IN_API_BODY]);
  };

  const flattenedData = useMemo(() => flattenPortfolioData3Level({ data: filteredData }), [filteredData]);
  const dataForColorEntities = useMemo(
    () => flattenPortfolioData3Level({ data: generalDashboardData }),
    [generalDashboardData]
  );

  const getRelatedItems = useCallback(
    ({ hoveredItem }) => {
      const config = hoveredItem?.config;
      const overriddenConfig = removeTheEffectOfGroupsFromConfig({ config });
      const overriddenHoveredItem = {
        ...hoveredItem,
        config: overriddenConfig,
      };

      const relatedItemsWithFlattenData = dynamicDashboardGetRelatedItems({
        data: flattenedData,
        hoveredItem: overriddenHoveredItem,
        IdColumnName,
      });
      return relatedItemsWithFlattenData;
    },
    [IdColumnName, flattenedData]
  );

  const isItemHovered = useCallback(
    (props) => {
      const overridenProps = removeTheEffectOfGroupsFromConfig({ config: props });
      return dynamicDashboardIsItemHovered({ ...(overridenProps ?? {}), IdColumnName });
    },
    [IdColumnName]
  );

  const getColorEntityDecorated = ({ chartType, dataPoint, diagrams, quantity }) => {
    const numericalColumnsUsedAsCategory = Object.values(allDataGroupedColumns)
      ?.map((group) => group.columns)
      ?.reduce((total, curr) => [...(total ?? []), ...(curr ?? [])]);

    if (numericalColumnsUsedAsCategory?.includes(quantity)) {
      return { categoryName: colorCategoriesEnums.COLUMNS, entityName: quantity };
    } else {
      return getColorEntity({ chartType, dataPoint, diagrams, quantity });
    }
  };

  const getColumnSortFunction = useCallback(({ acsending = true, aValue, bValue }) => {
    let output = defaultSortFunction(aValue, bValue);
    if (aValue === "A+" && ["A", "B", "C", "D", "E", "F", "G", "H"].includes(bValue))
      output = defaultSortFunction(0, 1);
    else if (bValue === "A+" && ["A", "B", "C", "D", "E", "F", "G", "H"].includes(aValue))
      output = defaultSortFunction(1, 0);
    return acsending ? output : -output;
  }, []);

  const lazyLoadRef = useRef(null);
  const onTabChange = useCallback((_) => {
    lazyLoadRef.current?.forceCheck();
  }, []);

  const getCustomAggregationFunction = useCallback(({ config }) => {
    const { chartType } = config;
    if (chartType === chartTypesEnums.SUMMARY) {
      if (config[settingsOptions.DATA_POINT] === columnsAPINamesEnums.TECHNICAL_DATA__HEATING_SYSTEM_SUMMARY) {
        return summarizeHeatingSystemData;
      } else if (config[settingsOptions.DATA_POINT] === columnsAPINamesEnums.TECHNICAL_DATA__COOLING_SYSTEM_SUMMARY) {
        return summarizeCoolingSystemData;
      } else if (config[settingsOptions.DATA_POINT] === columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_SUMMARY) {
        return summarizeWindowData;
      }
    }
    return null;
  }, []);

  const defaultColors = getDefaultColors();

  const theme = useTheme();
  return (
    <>
      <Helmet>
        <title>Portfolio | {theme.clientDisplayName}</title>
      </Helmet>
      <LoadingOrEmptyWrapper height="400px" showEmpty={isError} showLoading={isLoading || isSavedConfigLoading}>
        <DynamicDashboardMultiTab
          allAvailableFilters={tabFilters}
          allFilters={chartFilters}
          allSettings={allSettings}
          appName={APP_NAME_IN_REDUX}
          categoricalColumns={categoricalColumns}
          categoricalFilter
          dataForColorEntities={dataForColorEntities}
          dataTransformator={dataTransformator}
          defaultColors={defaultColors}
          defaultPortfolioConfigsAndColorsConfigs={defaultPortfolioConfigsAndColorsConfigs}
          dynamicChartsRowHeight={dynamicChartsRowHeight}
          filteredData={filteredData}
          filtersCategories={filtersCategories}
          generalDashboardData={generalDashboardData}
          getChartSettingsDependencies={(config) =>
            config.chartType !== chartTypesEnums.MINI_MAP
              ? undefined
              : [colorBarRange?.[config?.id]?.min, colorBarRange?.[config?.id]?.max]
          }
          getColorEntity={getColorEntityDecorated}
          getColumnDisplayName={getColumnDisplayNameCallback}
          getColumnSortFunction={getColumnSortFunction}
          getCustomAggregationFunction={getCustomAggregationFunction}
          getDataTransformatorMemoDependencyArray={getDataTransformatorMemoDependencyArray}
          getRelatedItems={getRelatedItems}
          getUnit={getUnit}
          IdColumnName={columnsAPINamesEnums.ID}
          isItemHovered={isItemHovered}
          miniMapObjectsNameKey={columnsAPINamesEnums.BUILDING_NAME}
          numericalColumns={numericalColumns}
          onReset={onReset}
          onSave={onSave}
          onTabChange={onTabChange}
          renderMiniMapObjectClickModal={renderMiniMapObjectClickModal}
          savedPortfolioAndColorsConfigs={savedPortfolioAndColorsConfigsMemoized}
          setFilteredData={setFilteredData}
          setTriggerFlag={setTriggerFlag}
          useGetSpecificData={useGetSpecificData}
        />
        <Paper className="mt-8">
          <h4 className="font-ds-headline-md ">
            {tableFilteredData?.length} {t("AssetOverview.filters.buildings")}
          </h4>
          <LazyLoad ref={lazyLoadRef}>
            <Table
              className="relative mt-6"
              columnsMinWidth="10rem"
              data={[...tableFilteredData]}
              headers={headers}
              idColumnName="id"
              onRowClick={(row) => {
                window.open(`${window.location.origin}/details/${row[columnsAPINamesEnums.ID]}/buildingProfile`);
              }}
            />
          </LazyLoad>
        </Paper>
      </LoadingOrEmptyWrapper>
    </>
  );
};
